import React, { FC } from 'react';
import { TableCell, TableRow, Typography, Tooltip } from '@mui/material';
import { truncateWithEllipsis } from 'utils/action-code';
import ActiveIndicator from 'components/users/shared/active-indicator/active-indicator.component';
import { ActionPrice } from 'models/organization';
import { useTranslation } from 'react-i18next';
import { formatMaterialMacroNames } from '../shared/format-material-macro-names';

type TenderedActionItemProps = {
    action: ActionPrice;
    onActionRowClick: (action: ActionPrice) => void;
    showPlanningColumn?: boolean;
    showValueColumn?: boolean;
};

type TemplateCountCellProps = {
    names: (string | null | undefined)[];
    countLabel: (count: number) => string;
    emptyLabel: string;
};

const TemplateCountCell: FC<TemplateCountCellProps> = ({ names, countLabel, emptyLabel }) => {
    const present = names.filter(Boolean) as string[];
    if (!present.length) {
        return (
            <Typography variant="body2" color="text.secondary">
                {emptyLabel}
            </Typography>
        );
    }
    return (
        <Tooltip
            title={
                <>
                    {present.map((name, i) => (
                        <div key={i}>{name}</div>
                    ))}
                </>
            }
            placement="top"
        >
            <Typography variant="body2" color="text.primary" sx={{ cursor: 'default' }}>
                {countLabel(present.length)}
            </Typography>
        </Tooltip>
    );
};

const TenderedActionItem: FC<TenderedActionItemProps> = ({ action, onActionRowClick }) => {
    const { t: translate } = useTranslation(['actions-materials']);

    const planNames = [
        action.productionTemplate?.planningDigitalTemplateId?.name,
        action.productionTemplate?.planningPhysicalTemplateId?.name,
    ];
    const valueNames = [
        action.productionTemplate?.valueDigitalTemplateId?.name,
        action.productionTemplate?.valuePhysicalTemplateId?.name,
    ];

    return (
        <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => onActionRowClick(action)}>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography variant="body2">{action.number}</Typography>
                {action.internalCode ? (
                    <Tooltip title={action.internalCode} placement="bottom-start">
                        <Typography variant="caption" color="text.secondary" display="block">
                            {truncateWithEllipsis(action.internalCode, 20)}
                        </Typography>
                    </Tooltip>
                ) : undefined}
            </TableCell>
            <TableCell>{action.name}</TableCell>
            <TableCell>{action.mainCategory}</TableCell>
            <TableCell>{action.subCategory}</TableCell>
            <TableCell>{formatMaterialMacroNames(action.materialMacros)}</TableCell>
            <TableCell>
                <TemplateCountCell
                    names={planNames}
                    countLabel={(n) => translate('actions-materials:N_PLANS', { count: n })}
                    emptyLabel={translate('actions-materials:NO_PLANS')}
                />
            </TableCell>
            <TableCell>
                <TemplateCountCell
                    names={valueNames}
                    countLabel={(n) => translate('actions-materials:N_TEMPLATES', { count: n })}
                    emptyLabel={translate('actions-materials:NO_VALUE_TEMPLATE')}
                />
            </TableCell>
            <TableCell sx={{ width: 10 }} align="center">
                <ActiveIndicator isActive={action.enabled} />
            </TableCell>
        </TableRow>
    );
};

export default TenderedActionItem;
